import {
  Button,
  CardHeader,
  Combobox,
  FileDropzone,
  FormErrorSummary,
  IconButton,
  Input,
  Modal,
  SegmentedControl,
  MultiSelect,
  Textarea,
  useSnackbar,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  GitCompareArrows,
  Images,
  Plus,
  Trash2,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import {
  EmptyCard,
  ErrorCard,
  ListFilterBar,
  LoadingCard,
  Pagination,
  RequestAlert,
} from '../../app/data-view.js';
import {
  recordsApi,
  getApiError,
  type ClientPhoto,
  type PhotoFilter,
  type SessionVisit,
} from '../../lib/api.js';
import { formatDateTime } from '../../lib/time-zone.js';
import { PhotoViewer } from './photo-viewer.js';

const PAGE_SIZE = 24;
const photoKey = (clientId: string) => ['company', 'records', 'photos', clientId] as const;

function PhotoContent({ photo, thumbnail = false }: { photo: ClientPhoto; thumbnail?: boolean }) {
  const [result, setResult] = useState<{ url?: string; error?: boolean }>({});
  useEffect(() => {
    let active = true;
    let url: string | undefined;
    void recordsApi
      .photoImage(photo.id, thumbnail)
      .then((blob) => {
        if (!active) return;
        url = URL.createObjectURL(blob);
        setResult({ url });
      })
      .catch(() => {
        if (active) setResult({ error: true });
      });
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [photo.id, thumbnail]);
  if (result.error)
    return (
      <p role="alert" className="text-sm text-danger">
        Não foi possível carregar esta foto.
      </p>
    );
  const url = result.url;
  if (!url) return <span className="text-xs text-content-muted">Carregando foto…</span>;
  return thumbnail ? (
    <img src={url} alt={photo.label} className="h-24 w-full rounded-lg object-contain" />
  ) : (
    <PhotoViewer key={photo.id} src={url} alt={photo.label} />
  );
}

/// O contador não limita a galeria. A leitura de imagens só ocorre depois de abrir Fotos.
export function SessionPhotosButton({ count, onOpen }: { count: number; onOpen: () => void }) {
  if (!count) return null;
  return (
    <IconButton label={`Fotos do atendimento (${count})`} icon={<Images />} onClick={onOpen} />
  );
}

export function ClientPhotos({
  clientId,
  timeZone,
  canManage,
  sessions = [],
  initialAppointmentId,
  onBack,
  compact = false,
}: {
  clientId: string;
  timeZone: string;
  canManage: boolean;
  sessions?: SessionVisit[];
  initialAppointmentId?: string | undefined;
  onBack?: (() => void) | undefined;
  compact?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [appointments, setAppointments] = useState<string[]>(
    initialAppointmentId ? [initialAppointmentId] : [],
  );
  const [services, setServices] = useState<string[]>([]);
  const [offset, setOffset] = useState(0);
  const [indices, setIndices] = useState<[number, number]>([0, 1]);
  const [compare, setCompare] = useState(false);
  const [target, setTarget] = useState<0 | 1>(0);
  const [upload, setUpload] = useState(false);
  const queryClient = useQueryClient();
  const top = useRef<HTMLDivElement>(null);
  const filter: PhotoFilter = {
    search,
    appointment: appointments,
    service: services,
    limit: PAGE_SIZE,
    offset,
  };
  const query = useQuery({
    queryKey: [...photoKey(clientId), filter],
    queryFn: () => recordsApi.listPhotos(clientId, filter),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const reset = () => {
    setOffset(0);
    setIndices([0, 1]);
  };
  const changed = async () => {
    await queryClient.invalidateQueries({ queryKey: ['company', 'records'] });
  };
  const serviceOptions = [
    ...new Map(
      sessions.map((s) => [s.serviceId, { value: s.serviceId, label: s.serviceDisplayName }]),
    ).values(),
  ];
  return (
    <div ref={top} className="grid min-w-0 gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CardHeader title="Fotos e evolução" icon={<Images className="size-4" />} />
        <div className="flex flex-wrap gap-2">
          {onBack ? (
            <Button size="sm" variant="outline" onClick={onBack}>
              <ArrowLeft className="size-4" /> Voltar
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            aria-pressed={compare}
            disabled={!query.data?.total}
            onClick={() => {
              setCompare(!compare);
              setTarget(0);
            }}
          >
            <GitCompareArrows className="size-4" />
            {compare ? 'Ver uma foto' : 'Comparar fotos'}
          </Button>
          {canManage ? (
            <Button size="sm" onClick={() => setUpload(true)}>
              <Plus className="size-4" /> Adicionar fotos
            </Button>
          ) : null}
        </div>
      </div>
      <ListFilterBar
        variant="panel"
        showViewPicker={false}
        searchLabel="Buscar fotos"
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          reset();
        }}
        count={`${query.data?.total ?? 0} fotos`}
      >
        {sessions.length ? (
          <MultiSelect
            itemNoun="atendimentos"
            label="Atendimentos"
            selected={appointments}
            options={sessions.map((s) => ({
              value: s.appointmentId,
              label: `${formatDateTime(s.startsAt, s.timeZone ?? 'UTC')} · ${s.serviceDisplayName}`,
            }))}
            onChange={(v) => {
              setAppointments(v);
              reset();
            }}
          />
        ) : null}
        {serviceOptions.length ? (
          <MultiSelect
            itemNoun="serviços"
            label="Serviços"
            selected={services}
            options={serviceOptions}
            onChange={(v) => {
              setServices(v);
              reset();
            }}
          />
        ) : null}
        {appointments.length || services.length ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setAppointments([]);
              setServices([]);
              reset();
            }}
          >
            Limpar filtros
          </Button>
        ) : null}
      </ListFilterBar>
      {query.isLoading ? (
        <LoadingCard label="Carregando fotos" />
      ) : query.error ? (
        <ErrorCard message={getApiError(query.error)} />
      ) : !query.data?.total ? (
        <EmptyCard
          icon={Images}
          title="Nenhuma foto encontrada"
          description="Adicione fotos do cliente ou de um atendimento para acompanhar a evolução."
        />
      ) : (
        <>
          <div
            className={
              compare ? `grid min-w-0 gap-4 ${compact ? '' : 'lg:grid-cols-2'}` : 'min-w-0'
            }
          >
            {([0, ...(compare ? [1] : [])] as Array<0 | 1>).map((side) => (
              <PhotoSelection
                key={side}
                clientId={clientId}
                filter={filter}
                index={Math.min(indices[side], query.data.total - 1)}
                total={query.data.total}
                side={side}
                timeZone={timeZone}
                canManage={canManage}
                onIndex={(i) => setIndices((prev) => (side === 0 ? [i, prev[1]] : [prev[0], i]))}
                onRemoved={async () => {
                  reset();
                  await changed();
                }}
              />
            ))}
          </div>
          {compare ? (
            <SegmentedControl
              label="Destino da seleção de fotos"
              value={String(target)}
              onChange={(value) => setTarget(value === '0' ? 0 : 1)}
              options={[
                {
                  value: '0',
                  label: 'Foto principal',
                  accessibleLabel: 'Selecionar foto principal',
                },
                {
                  value: '1',
                  label: 'Foto de comparação',
                  accessibleLabel: 'Selecionar foto de comparação',
                },
              ]}
            />
          ) : null}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {query.data.photos.map((photo, i) => (
              <button
                key={photo.id}
                type="button"
                aria-label={`${photo.label} · ${photo.serviceDisplayName ?? 'Sem atendimento'}`}
                aria-pressed={indices[target] === offset + i}
                className="grid min-w-0 cursor-pointer gap-2 rounded-xl border border-surface-border bg-surface-card p-2 text-left text-xs aria-pressed:border-brand-500 aria-pressed:ring-1 aria-pressed:ring-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                onClick={() =>
                  setIndices((prev) =>
                    target === 0 ? [offset + i, prev[1]] : [prev[0], offset + i],
                  )
                }
              >
                <PhotoContent photo={photo} thumbnail />
                <span className="break-words font-semibold">{photo.label}</span>
                <span className="text-content-muted">
                  {photo.serviceDisplayName ?? 'Sem atendimento'}
                </span>
              </button>
            ))}
          </div>
          <Pagination
            total={query.data.total}
            limit={PAGE_SIZE}
            offset={offset}
            onOffsetChange={setOffset}
            itemLabel={() => 'fotos'}
          />
        </>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={() =>
          top.current?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
              ? 'instant'
              : 'smooth',
            block: 'start',
          })
        }
      >
        Voltar ao topo
      </Button>
      {upload ? (
        <PhotoUpload
          clientId={clientId}
          sessions={sessions}
          initialAppointmentId={initialAppointmentId}
          onClose={() => setUpload(false)}
          onSaved={async () => {
            reset();
            await changed();
          }}
        />
      ) : null}
    </div>
  );
}

function PhotoSelection({
  clientId,
  filter,
  index,
  total,
  side,
  timeZone,
  canManage,
  onIndex,
  onRemoved,
}: {
  clientId: string;
  filter: PhotoFilter;
  index: number;
  total: number;
  side: number;
  timeZone: string;
  canManage: boolean;
  onIndex: (index: number) => void;
  onRemoved: () => Promise<void>;
}) {
  const query = useQuery({
    queryKey: [...photoKey(clientId), { ...filter, limit: 1, offset: index }],
    queryFn: () => recordsApi.listPhotos(clientId, { ...filter, limit: 1, offset: index }),
    retry: false,
  });
  const [removing, setRemoving] = useState(false);
  const photo = query.data?.photos[0];
  return (
    <section
      className="grid min-w-0 content-start gap-3"
      aria-label={side === 0 ? 'Foto principal' : 'Foto de comparação'}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          {side === 0 ? 'Foto principal' : 'Foto de comparação'}
        </h3>
        <div className="flex items-center gap-2">
          <IconButton
            label={`Foto anterior (${side + 1})`}
            icon={<ChevronLeft />}
            disabled={index === 0}
            onClick={() => onIndex(index - 1)}
          />
          <span aria-live="polite" className="text-xs tabular-nums">
            {index + 1} de {total}
          </span>
          <IconButton
            label={`Próxima foto (${side + 1})`}
            icon={<ChevronRight />}
            disabled={index + 1 >= total}
            onClick={() => onIndex(index + 1)}
          />
        </div>
      </div>
      {query.error ? (
        <ErrorCard message={getApiError(query.error)} />
      ) : !photo ? (
        <LoadingCard label="Carregando foto" />
      ) : (
        <>
          <PhotoContent key={photo.id} photo={photo} />
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="break-words text-sm font-semibold">{photo.label}</p>
              <p className="text-xs text-content-muted">
                {photo.serviceDisplayName ?? 'Sem atendimento vinculado'}
                {photo.appointmentStartsAt
                  ? ` · ${formatDateTime(photo.appointmentStartsAt, photo.appointmentTimeZone ?? timeZone)}`
                  : ''}
              </p>
              <p className="text-xs text-content-muted">
                Adicionada em {formatDateTime(photo.createdAt, timeZone)} por{' '}
                {photo.createdByDisplayName}
              </p>
              {photo.notes ? (
                <p className="whitespace-pre-wrap break-words text-sm">{photo.notes}</p>
              ) : null}
            </div>
            {canManage ? (
              <IconButton
                label="Retirar foto da galeria"
                icon={<Trash2 />}
                tone="danger"
                onClick={() => setRemoving(true)}
              />
            ) : null}
          </div>
          {removing ? (
            <PhotoRemoval photo={photo} onClose={() => setRemoving(false)} onSaved={onRemoved} />
          ) : null}
        </>
      )}
    </section>
  );
}

function PhotoUpload({
  clientId,
  sessions,
  initialAppointmentId,
  onClose,
  onSaved,
}: {
  clientId: string;
  sessions: SessionVisit[];
  initialAppointmentId: string | undefined;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const options = sessions.map((s) => ({
    value: s.appointmentId,
    label: `${formatDateTime(s.startsAt, s.timeZone ?? 'UTC')} · ${s.serviceDisplayName}`,
  }));
  if (initialAppointmentId && !options.some((option) => option.value === initialAppointmentId))
    options.unshift({ value: initialAppointmentId, label: 'Atendimento aberto na Agenda' });
  const formId = useId();
  const [attempted, setAttempted] = useState(false);
  const [files, setFiles] = useState<File[]>([]),
    [label, setLabel] = useState(''),
    [notes, setNotes] = useState('');
  const [appointment, setAppointment] = useState(initialAppointmentId ?? ''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<string>();
  const snackbar = useSnackbar();
  const pending = useRef<File[]>([]);
  const uploadIds = useRef(new Map<File, string>());
  async function save() {
    setAttempted(true);
    if (!files.length || !label.trim()) {
      setError('Escolha ao menos uma foto e informe a descrição.');
      return;
    }
    setBusy(true);
    setError(undefined);
    pending.current = pending.current.length ? pending.current : [...files];
    try {
      while (pending.current[0]) {
        const file = pending.current[0];
        if (!uploadIds.current.has(file)) uploadIds.current.set(file, crypto.randomUUID());
        await recordsApi.uploadPhoto(clientId, file, {
          uploadId: uploadIds.current.get(file)!,
          appointmentId: appointment || null,
          label,
          notes: notes || null,
        });
        pending.current.shift();
        setFiles([...pending.current]);
      }
      await onSaved();
      snackbar.success('Fotos adicionadas.');
      onClose();
    } catch (e) {
      setError(
        `${getApiError(e)} As fotos já enviadas foram preservadas; tente novamente para enviar somente as restantes.`,
      );
      await onSaved();
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={(v) => {
        if (!v && !busy) onClose();
      }}
      title="Adicionar fotos"
      description="Fotos privadas do prontuário. JPEG, PNG ou WebP, até 10 MB por arquivo."
      footer={
        <>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Cancelar
          </Button>
          <FormErrorSummary form={formId} />
          <Button type="submit" form={formId} loading={busy}>
            Adicionar fotos
          </Button>
        </>
      }
    >
      <form
        id={formId}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
        className="grid gap-4"
      >
        <FileDropzone
          error={attempted && !files.length ? 'Escolha ao menos uma foto.' : undefined}
          label="Fotos"
          files={files}
          accept="image/jpeg,image/png,image/webp"
          maxSizeBytes={10 * 1024 * 1024}
          chooseText="Escolher fotos"
          dropText="Arraste as fotos para cá"
          hint={
            files.length
              ? `${files.length} ${files.length === 1 ? 'foto pendente' : 'fotos pendentes'} de envio.`
              : 'Você pode escolher ou arrastar várias fotos de uma vez.'
          }
          disabled={busy}
          onFilesChange={(next) => {
            setFiles(next);
            pending.current = [];
          }}
        />
        <Input
          error={attempted && !label.trim() ? 'Informe a descrição.' : undefined}
          label="Descrição"
          value={label}
          maxLength={160}
          disabled={busy}
          onChange={(e) => setLabel(e.target.value)}
        />
        <Textarea
          label="Observações (opcional)"
          value={notes}
          maxLength={2000}
          disabled={busy}
          onChange={(e) => setNotes(e.target.value)}
        />
        <Combobox
          label="Atendimento (opcional)"
          placeholder="Buscar atendimento"
          selectedLabel={
            options.find((option) => option.value === appointment)?.label ??
            'Sem atendimento vinculado'
          }
          onSelect={(choice) => {
            if (choice.kind === 'option') setAppointment(choice.value);
          }}
          onClear={() => setAppointment('')}
          disabled={busy}
          options={[{ value: '', label: 'Sem atendimento vinculado' }, ...options]}
        />
        {error ? <RequestAlert message={error} /> : null}
      </form>
    </Modal>
  );
}

function PhotoRemoval({
  photo,
  onClose,
  onSaved,
}: {
  photo: ClientPhoto;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const formId = useId();
  const [attempted, setAttempted] = useState(false);
  const [reason, setReason] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<string>();
  const snackbar = useSnackbar();
  async function remove() {
    setAttempted(true);
    if (!reason.trim()) {
      setError('Informe o motivo da retirada.');
      return;
    }
    setBusy(true);
    try {
      await recordsApi.removePhoto(photo.id, reason);
      await onSaved();
      snackbar.success('Foto retirada da galeria.');
      onClose();
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={(v) => {
        if (!v && !busy) onClose();
      }}
      title="Retirar foto da galeria"
      description="A imagem deixa de aparecer. O registro e o motivo ficam preservados no prontuário."
      footer={
        <>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Cancelar
          </Button>
          <FormErrorSummary form={formId} />
          <Button type="submit" form={formId} loading={busy}>
            Retirar foto
          </Button>
        </>
      }
    >
      <form
        id={formId}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void remove();
        }}
      >
        <Textarea
          error={attempted && !reason.trim() ? 'Informe o motivo.' : undefined}
          label="Motivo"
          maxLength={2000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {error ? <RequestAlert message={error} /> : null}
      </form>
    </Modal>
  );
}
